/**
 * POSOfflineIndicator — small status badge for the POS screen showing:
 *   - online/offline status
 *   - number of sales pending sync (from the outbox)
 *   - last sync time
 *   - "Sync now" button when applicable
 *
 * Mount near the top of POSScreen. Self-contained — uses connectivity hook
 * + sync worker status listeners.
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiWifi, FiWifiOff, FiRefreshCw, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { useOnline } from '../utils/connectivity';
import { getPendingCount, onSyncWorkerStatus, drainOutbox } from '../utils/posSyncWorker';
import { onSyncStatusChange, getMinutesSinceLastSync } from '../utils/posMasterSync';

const POSOfflineIndicator = () => {
  const online = useOnline();
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Refresh pending count periodically + on outbox changes
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const n = await getPendingCount();
        if (alive) setPending(n);
      } catch { /* ignore */ }
    };
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => { alive = false; clearInterval(timer); };
  }, []);

  // Listen to sync worker events
  useEffect(() => {
    const unsub = onSyncWorkerStatus((s) => {
      if (s.phase === 'draining') setSyncing(true);
      if (s.phase === 'completed' || s.phase === 'error') {
        setSyncing(false);
        setLastResult(s);
        // Refresh pending count after a drain attempt
        getPendingCount().then(setPending).catch(() => {});
      }
    });
    return unsub;
  }, []);

  // Listen to master-sync events too (just for the spinner — pending count
  // doesn't change with master sync)
  useEffect(() => {
    const unsub = onSyncStatusChange((s) => {
      if (s.phase === 'running') setSyncing(true);
      if (s.phase === 'completed' || s.phase === 'error') setSyncing(false);
    });
    return unsub;
  }, []);

  const handleSyncNow = () => {
    if (!online) return;
    // force=true: reset retry counters on stuck rows so the user's explicit
    // "Sync now" click actually retries — the periodic auto-drain respects
    // back-off, but manual clicks shouldn't be silently ignored.
    drainOutbox({ force: true }).catch(() => {});
  };

  // ─── Render variants ──

  // Offline + pending → loud warning
  if (!online && pending > 0) {
    return (
      <Bar $tone="warning">
        <FiWifiOff size={16} />
        <Text>
          <strong>Offline mode</strong> — {pending} sale{pending === 1 ? '' : 's'} pending sync.
          Will auto-upload when internet returns.
        </Text>
      </Bar>
    );
  }

  // Offline, no pending → tiny matching badge (same compact style as the Online dot)
  if (!online) {
    return (
      <Bar $tone="warning" $compact>
        <FiWifiOff size={14} />
        <SmallText>Offline</SmallText>
      </Bar>
    );
  }

  // Online + pending → blue banner with Sync now button
  if (online && pending > 0) {
    // If the last drain bailed out because there's no real Firebase session
    // (user is in offline-auth mode), tell them what to do instead of just
    // re-presenting the same "Sync now" button that won't work.
    const needsReauth = lastResult?.phase === 'completed' && lastResult?.reason === 'no-firebase-auth-token';
    return (
      <Bar $tone={needsReauth ? 'warning' : 'info'}>
        <FiRefreshCw size={16} className={syncing ? 'spin' : ''} />
        <Text>
          {syncing
            ? <>Syncing {pending} pending sale{pending === 1 ? '' : 's'}…</>
            : needsReauth
              ? <>{pending} sale{pending === 1 ? '' : 's'} pending — please log out and log back in online to sync.</>
              : <>{pending} sale{pending === 1 ? '' : 's'} pending sync</>}
        </Text>
        {!syncing && !needsReauth && <SyncBtn onClick={handleSyncNow}>Sync now</SyncBtn>}
      </Bar>
    );
  }

  // Online + no pending + recent fail → small warning
  if (online && lastResult?.failed > 0) {
    return (
      <Bar $tone="error">
        <FiAlertTriangle size={16} />
        <Text>{lastResult.failed} sale{lastResult.failed === 1 ? '' : 's'} failed to sync. Check console.</Text>
        <SyncBtn onClick={handleSyncNow}>Retry</SyncBtn>
      </Bar>
    );
  }

  // Online + nothing pending → tiny "online" dot (or hide entirely)
  return (
    <Bar $tone="success" $compact>
      <FiCheckCircle size={14} />
      <SmallText>Online</SmallText>
    </Bar>
  );
};

export default POSOfflineIndicator;

// ─── styles ─────────────
const TONE_BG = {
  info:    '#1e3a8a',  // blue
  warning: '#92400e',  // amber
  error:   '#7f1d1d',  // red
  success: '#14532d'   // green
};
const TONE_FG = {
  info: '#fff', warning: '#fff', error: '#fff', success: '#fff'
};

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${p => TONE_BG[p.$tone] || TONE_BG.info};
  color: ${p => TONE_FG[p.$tone] || '#fff'};
  padding: ${p => p.$compact ? '4px 10px' : '8px 14px'};
  font-size: ${p => p.$compact ? '11px' : '13px'};
  border-radius: ${p => p.$compact ? '12px' : '6px'};
  margin: ${p => p.$compact ? '0' : '0 0 8px 0'};
  width: ${p => p.$compact ? 'fit-content' : 'auto'};

  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
`;

const Text = styled.span` flex: 1; `;
const SmallText = styled.span` font-size: 11px; `;

const SyncBtn = styled.button`
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: rgba(255, 255, 255, 0.28); }
`;
